// components/ViewOnlyPortfolioSummary.js
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { Grid, Box, Typography, CircularProgress, Card, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DoughnutChart from '../components/DoughnutChart';
import SectorDoughnutChart from '../components/SectorDoughnutChart';
import useForexData from '../hooks/useForexData';

const ViewOnlyPortfolioSummary = ({ holdings, portfolioInfo, summary, sectorAllocation }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const baseCurrency = portfolioInfo?.baseCurrency || 'USD';
  const [sectorData, setSectorData] = useState(null);
  const [loadingSectors, setLoadingSectors] = useState(false);

  // Extract unique base currencies
  const uniqueBaseCurrencies = useMemo(() => {
    return [...new Set([baseCurrency])];
  }, [baseCurrency]);

  // Use the forex hook to get conversion rates
  const { forexRates, loading: forexLoading, error: forexError } = useForexData(uniqueBaseCurrencies);

  // Currency conversion function
  const convertCurrency = useCallback((amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;

    // Standardize currency codes
    const standardizedFromCurrency = fromCurrency === 'ILA' ? 'ILS' : fromCurrency;
    const standardizedToCurrency = toCurrency === 'ILA' ? 'ILS' : toCurrency;

    const rate = forexRates[`${standardizedFromCurrency}/${standardizedToCurrency}`];
    return rate ? amount * rate : amount;
  }, [forexRates]);

  // Handle sector data from the correct source
  useEffect(() => {
    // sectorAllocation is directly passed as a prop
    if (sectorAllocation) {
      setSectorData(sectorAllocation);
    }
    setLoadingSectors(false);
  }, [sectorAllocation]);

  // Calculate summary with currency conversion
  const summaryData = useMemo(() => {
    if (forexLoading || !forexRates) {
      return null;
    }

    let totalValue = 0;
    let dailyChange = 0;
    const portfolioData = {};
    let bestPerformer = null;
    let worstPerformer = null;

    holdings.forEach(holding => {
      // Get values from the holding
      const value = holding.totalValue || holding.quantity * holding.price;
      const dailyChangeAmount = holding.dailyChangeAmount || 0;
      const currency = holding.currency || 'USD';

      // Convert to base currency
      const convertedValue = convertCurrency(value, currency, baseCurrency);
      const convertedDailyChange = convertCurrency(dailyChangeAmount, currency, baseCurrency);

      // Add to totals
      totalValue += convertedValue;
      dailyChange += convertedDailyChange;

      // Store value for chart
      portfolioData[holding.symbol] = convertedValue;

      // Track performers based on PnL amount
      const pnlAmount = holding.pnlAmount ||
        (holding.totalValue - (holding.avgPrice * holding.quantity)) || 0;

      // Convert to base currency for consistent comparison
      const convertedPnlAmount = convertCurrency(pnlAmount, holding.currency, baseCurrency);

      // Only track as best performer if it's actually profitable (positive PnL)
      if (convertedPnlAmount > 0 && (!bestPerformer || convertedPnlAmount > bestPerformer.performanceMetric)) {
        bestPerformer = {
          symbol: holding.symbol,
          name: holding.name,
          performanceMetric: convertedPnlAmount,
        };
      }

      // Only track as worst performer if it's actually losing money (negative PnL)
      if (convertedPnlAmount < 0 && (!worstPerformer || convertedPnlAmount < worstPerformer.performanceMetric)) {
        worstPerformer = {
          symbol: holding.symbol,
          name: holding.name,
          performanceMetric: convertedPnlAmount,
        };
      }
    });

    return {
      totalValue,
      totalDailyChange: dailyChange,
      portfolioData,
      bestPerformer,
      worstPerformer
    };
  }, [holdings, forexRates, forexLoading, baseCurrency, convertCurrency]);

  // Format currency values
  const formatCurrency = (value) => {
    if (typeof value !== 'number') return 'N/A';
    
    // Format large numbers more compactly for better display
    const absValue = Math.abs(value);
    if (absValue >= 1000000000) {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: baseCurrency,
        notation: 'compact',
        maximumFractionDigits: 2
      }).format(value);
    } else if (absValue >= 1000000) {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: baseCurrency,
        notation: 'compact',
        maximumFractionDigits: 2
      }).format(value);
    }
    
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: baseCurrency 
    }).format(value);
  };

  // Render performer details in compact format
  const renderPerformer = (performer, type) => {
    if (!performer) return 'N/A';
    const color = type === 'best' ? 'green' : 'red';
    return (
      <>
        <Typography variant="body2" fontWeight="bold">
          {performer.symbol}
        </Typography>
        <Typography 
          variant="body2" 
          color={color}
          noWrap={false}
          sx={{ 
            wordBreak: 'break-word'
          }}
        >
          {formatCurrency(performer.performanceMetric)}
        </Typography>
      </>
    );
  };

  // Show loading state while forex data is loading
  if (forexLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error if forex data couldn't be loaded
  if (forexError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Error loading currency data. Values shown may not be converted correctly.
        </Typography>
      </Box>
    );
  }

  // If summary data isn't ready yet, show loading
  if (!summaryData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Determine grid layout based on screen size
  const totalValueGridSize = isMobile ? 12 : 3;
  const performanceGridSize = isMobile ? 4 : 3;

  return (
    <Grid container spacing={2} mt={2}>
      {/* First row: Total Value and Performance Metrics */}
      <Grid container item spacing={2}>
        {/* Total Value */}
        <Grid item xs={totalValueGridSize}>
          <Box sx={{
            boxShadow: 3,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: isMobile ? 'auto' : '100%',
            minHeight: '120px',
            bgcolor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius
          }}>
            <Typography variant="body1" fontWeight="bold">Total Value</Typography>
            <Typography 
              variant={isMobile ? "h5" : "h4"}
              sx={{ 
                wordBreak: 'break-word',
                width: '100%',
                textAlign: 'center'
              }}
            >
              {formatCurrency(summaryData.totalValue)}
            </Typography>
          </Box>
        </Grid>

        {/* Performance Metrics - Now in smaller boxes */}
        <Grid item xs={performanceGridSize}>
          <Card sx={{
            boxShadow: 3,
            p: 1.5,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            bgcolor: theme.palette.background.paper
          }}>
            <Typography variant="body2" fontWeight="bold" align="center">Best Performer</Typography>
            <Box sx={{ textAlign: 'center' }}>
              {renderPerformer(summaryData.bestPerformer, 'best')}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={performanceGridSize}>
          <Card sx={{
            boxShadow: 3,
            p: 1.5,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            bgcolor: theme.palette.background.paper
          }}>
            <Typography variant="body2" fontWeight="bold" align="center">Worst Performer</Typography>
            <Box sx={{ textAlign: 'center' }}>
              {renderPerformer(summaryData.worstPerformer, 'worst')}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={performanceGridSize}>
          <Card sx={{
            boxShadow: 3,
            p: 1.5,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            bgcolor: theme.palette.background.paper
          }}>
            <Typography variant="body2" fontWeight="bold" align="center">Daily Change</Typography>
            <Typography
              variant="body1"
              align="center"
              color={summaryData.totalDailyChange > 0 ? 'green' : summaryData.totalDailyChange < 0 ? 'red' : 'inherit'}
              sx={{ 
                wordBreak: 'break-word'
              }}
            >
              {formatCurrency(summaryData.totalDailyChange)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Second row: Charts side by side */}
      <Grid container item spacing={2} mt={1}>
        {/* Asset Allocation Chart */}
        <Grid item xs={isMobile ? 12 : 6}>
          <Box sx={{
            boxShadow: 3,
            p: 2,
            height: '300px',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.paper
          }}>
            <Typography variant="h6" gutterBottom>Allocation ({baseCurrency})</Typography>
            {Object.keys(summaryData.portfolioData || {}).length > 0 ? (
              <Box sx={{ height: '250px', position: 'relative' }}>
                <DoughnutChart
                  data={summaryData.portfolioData}
                  headline=""
                  currency={baseCurrency}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography>No data available</Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Sector Allocation Chart */}
        <Grid item xs={isMobile ? 12 : 6}>
          <Box sx={{
            boxShadow: 3,
            p: 2,
            height: '300px',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.paper
          }}>
            <Typography variant="h6" gutterBottom>Sector Allocation</Typography>
            {loadingSectors ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : sectorData && Object.keys(sectorData).length > 0 ? (
              <Box sx={{ height: '250px', position: 'relative' }}>
                <SectorDoughnutChart
                  sectorData={sectorData}
                  title=""
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography>No sector data available</Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ViewOnlyPortfolioSummary;